import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Loader2, FileText, CheckCircle, XCircle, Clock, ArrowUp, Trash2 } from "lucide-react";

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY (Italian format)
const formatDateIT = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Helper function to check if user has manager-level visibility
const isManagerRole = (ruolo) => ruolo === "titolare" || ruolo === "amministrativo";

export default function RequestsPage() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loadMoreCount, setLoadMoreCount] = useState(5);
  const [newRequest, setNewRequest] = useState({
    tipo: "ferie",
    data_inizio: "",
    data_fine: "",
    ora_inizio: "",
    ora_fine: "",
    note: ""
  });

  useEffect(() => {
    fetchRequests();
  }, [token]);

  // Update filter when URL params change
  useEffect(() => {
    const urlFilter = searchParams.get("filter");
    if (urlFilter) {
      setFilter(urlFilter);
    }
  }, [searchParams]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/leave-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
      setVisibleCount(5); // Reset visible count when fetching new data
    } catch (error) {
      toast.error("Errore nel caricamento delle richieste");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leave-requests`, newRequest, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta inviata con successo!");
      setNewRequestOpen(false);
      setNewRequest({ tipo: "ferie", data_inizio: "", data_fine: "", note: "" });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRequest = async (requestId, stato) => {
    try {
      await axios.put(`${API}/leave-requests/${requestId}`, { stato, note_risposta: "" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Richiesta ${stato === "approvato" ? "approvata" : "rifiutata"}!`);
      fetchRequests();
    } catch (error) {
      toast.error("Errore nell'aggiornamento della richiesta");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Sei sicuro di voler cancellare questa richiesta?")) {
      return;
    }
    try {
      await axios.delete(`${API}/leave-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Richiesta cancellata con successo!");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella cancellazione della richiesta");
    }
  };

  const filteredRequests = requests.filter((r) => {
    if (filter === "all") return true;
    return r.stato === filter;
  });

  const visibleRequests = filteredRequests.slice(0, visibleCount);
  const remainingCount = filteredRequests.length - visibleCount;

  const handleLoadMore = () => {
    const newCount = Math.min(visibleCount + loadMoreCount, filteredRequests.length);
    setVisibleCount(newCount);
  };

  // Reset visible count when filter changes
  useEffect(() => {
    setVisibleCount(5);
  }, [filter]);

  const getStatusBadge = (stato) => {
    switch (stato) {
      case "in_attesa":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="h-3 w-3 mr-1" />In attesa</Badge>;
      case "approvato":
        return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle className="h-3 w-3 mr-1" />Approvato</Badge>;
      case "rifiutato":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Rifiutato</Badge>;
      default:
        return <Badge>{stato}</Badge>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-testid="requests-title">
              {isManagerRole(user?.ruolo) ? "Tutte le Richieste" : "Le Mie Richieste"}
            </h1>
            <p className="text-slate-500 mt-1">
              {isManagerRole(user?.ruolo) ? "Visualizza le richieste dei dipendenti" : "Visualizza e gestisci le tue richieste"}
            </p>
          </div>
          <div className="hidden">
            <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
              <DialogTrigger asChild>
                <Button className="btn-theme btn-press" data-testid="new-request-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Richiesta
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuova Richiesta</DialogTitle>
                  <DialogDescription>Compila i campi per inviare una richiesta.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateRequest} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={newRequest.tipo} onValueChange={(v) => setNewRequest({ ...newRequest, tipo: v, data_fine: v === "permesso" ? "" : newRequest.data_fine })}>
                      <SelectTrigger data-testid="request-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ferie">Ferie</SelectItem>
                        <SelectItem value="permesso">Permesso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date fields - different for ferie vs permesso */}
                  {newRequest.tipo === "ferie" ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data inizio</Label>
                        <Input
                          type="date"
                          value={newRequest.data_inizio}
                          onChange={(e) => setNewRequest({ ...newRequest, data_inizio: e.target.value })}
                          required
                          data-testid="request-data-inizio"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data fine</Label>
                        <Input
                          type="date"
                          value={newRequest.data_fine}
                          onChange={(e) => setNewRequest({ ...newRequest, data_fine: e.target.value })}
                          required
                          data-testid="request-data-fine"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Data</Label>
                        <Input
                          type="date"
                          value={newRequest.data_inizio}
                          onChange={(e) => setNewRequest({ ...newRequest, data_inizio: e.target.value, data_fine: e.target.value })}
                          required
                          data-testid="request-data-inizio"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Dalle ore</Label>
                          <Input
                            type="time"
                            value={newRequest.ora_inizio}
                            onChange={(e) => setNewRequest({ ...newRequest, ora_inizio: e.target.value })}
                            required
                            data-testid="request-ora-inizio"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Alle ore</Label>
                          <Input
                            type="time"
                            value={newRequest.ora_fine}
                            onChange={(e) => setNewRequest({ ...newRequest, ora_fine: e.target.value })}
                            required
                            data-testid="request-ora-fine"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Note (opzionale)</Label>
                    <Textarea
                      placeholder="Aggiungi una nota..."
                      value={newRequest.note}
                      onChange={(e) => setNewRequest({ ...newRequest, note: e.target.value })}
                      data-testid="request-note"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-theme" disabled={submitting} data-testid="submit-request-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Invia Richiesta
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Tabs defaultValue="all" value={filter} onValueChange={setFilter}>
          <TabsList className="tabs-theme-list">
            <TabsTrigger value="all" className="tabs-theme-trigger" data-testid="filter-all">Tutte</TabsTrigger>
            <TabsTrigger value="in_attesa" className="tabs-theme-trigger" data-testid="filter-pending">In attesa</TabsTrigger>
            <TabsTrigger value="approvato" className="tabs-theme-trigger" data-testid="filter-approved">Approvate</TabsTrigger>
            <TabsTrigger value="rifiutato" className="tabs-theme-trigger" data-testid="filter-rejected">Rifiutate</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Requests List */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Nessuna richiesta trovata</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visibleRequests.map((request, index) => (
                  <div
                    key={request.id}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 gap-4 animate-fade-in hover:bg-slate-50 transition-colors`}
                    data-testid={`request-item-${request.id}`}
                  >
                    <div className="space-y-1 flex-1">
                      {isManagerRole(user?.ruolo) && (
                        <p className="font-semibold text-slate-900">
                          {request.user_nome} {request.user_cognome}
                          {request.user_posizione && (
                            <span className="ml-2 text-sm font-normal text-slate-500">({request.user_posizione})</span>
                          )}
                        </p>
                      )}
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-slate-700">
                          {request.tipo === "ferie" ? "Ferie" : "Permesso"}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-600">
                          {request.tipo === "ferie" ? (
                            <>{formatDateIT(request.data_inizio)} → {formatDateIT(request.data_fine)}</>
                          ) : (
                            <>{formatDateIT(request.data_inizio)} {request.ora_inizio && request.ora_fine ? `dalle ${request.ora_inizio} alle ${request.ora_fine}` : ""}</>
                          )}
                        </span>
                      </div>
                      {request.note && (
                        <p className="text-sm text-slate-400 italic">"{request.note}"</p>
                      )}
                      {request.note_risposta && (
                        <p className="text-sm text-slate-500">Risposta: {request.note_risposta}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {getStatusBadge(request.stato)}
                      {/* Only titolare can approve/reject - not amministrativo */}
                      {user?.ruolo === "titolare" && request.stato === "in_attesa" && (
                        <div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                          <Button
                            size="sm"
                            onClick={() => handleUpdateRequest(request.id, "approvato")}
                            className="bg-emerald-600 hover:bg-emerald-700 btn-press flex-1 sm:flex-none"
                            data-testid={`approve-btn-${request.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            <span>Approva</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateRequest(request.id, "rifiutato")}
                            className="border-red-200 text-red-600 hover:bg-red-50 btn-press flex-1 sm:flex-none"
                            data-testid={`reject-btn-${request.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            <span>Rifiuta</span>
                          </Button>
                        </div>
                      )}
                      {/* Dipendente can delete their own pending requests */}
                      {user?.ruolo === "dipendente" && request.stato === "in_attesa" && request.user_id === user?.id && (
                        <div className="flex flex-col gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteRequest(request.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50 btn-press"
                            data-testid={`delete-btn-${request.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            <span>Cancella</span>
                          </Button>
                          <p className="text-xs text-slate-400 italic">
                            Hai sbagliato le date o il messaggio? Puoi cancellare e rifare la richiesta.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Load More Section */}
                {remainingCount > 0 && (
                  <div className="flex items-center justify-center gap-3 p-4 bg-slate-50">
                    <span className="text-sm text-slate-500">Visualizza altre</span>
                    <Select
                      value={loadMoreCount.toString()}
                      onValueChange={(value) => setLoadMoreCount(parseInt(value))}
                    >
                      <SelectTrigger className="w-20 h-8" data-testid="load-more-select-requests">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 5, 10, 15, 20, 25, 50, 99].map((n) => (
                          <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMore}
                      className="gap-2"
                      data-testid="load-more-btn-requests"
                    >
                      Carica ({remainingCount} rimanenti)
                    </Button>
                  </div>
                )}
                
                {/* Back to Top Button - shown when all items loaded and list is long */}
                {remainingCount <= 0 && visibleRequests.length > 5 && (
                  <div className="flex justify-center p-4 bg-slate-50">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="gap-2"
                      data-testid="back-to-top-btn-requests"
                    >
                      <ArrowUp className="h-4 w-4" />
                      Torna su
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
